import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../lib/apiBase";

const API = API_BASE + "/api";

const CUSTOM_COLOR_PALETTE = [
  "#ec4899", "#00c8ff", "#f59e0b", "#10b981",
  "#ef4444", "#a855f7", "#6b7280", "#3b82f6",
  "#84cc16", "#06b6d4",
];

/**
 * Bottom sheet modal that lets the user pick a tag for a conversation.
 * Loads predefined + custom labels from the backend on open.
 * Allows creating a new custom tag (name + color).
 *
 * Props:
 *   conv       — { conv_id, tag }
 *   onClose    — () => void
 *   onPatch    — (convId, patch) => Promise<void>  (will be called with { tag } or { tag: { name: "" } } to clear)
 */
export default function LabelsModal({ conv, onClose, onPatch }) {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(CUSTOM_COLOR_PALETTE[0]);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/legal/labels`)
      .then((resp) => {
        if (alive) setLabels(resp?.data?.labels || []);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const close = () => {
    setClosing(true);
    setTimeout(onClose, 180);
  };

  // Manage mode: when there's no conv_id, the modal is used to manage labels
  // (view all, create new custom, delete custom). No assignment happens.
  // Bulk mode (when conv.conv_id === "__bulk__" and __bulkIds set) bypasses manage and applies to all.
  const isManageMode = (!conv?.conv_id || conv?.conv_id === null) && !Array.isArray(conv?.__bulkIds);

  const pickTag = async (label) => {
    if (!label) return;
    if (isManageMode) { close(); return; }
    // Bulk apply: when the synthetic conv carries __bulkIds, patch each
    if (Array.isArray(conv?.__bulkIds) && conv.__bulkIds.length > 0) {
      await Promise.all(
        conv.__bulkIds.map((id) =>
          onPatch(id, { tag: { name: label.name, color: label.color } })
        )
      );
      close();
      return;
    }
    await onPatch(conv.conv_id, {
      tag: { name: label.name, color: label.color },
    });
    close();
  };

  const clearTag = async () => {
    await onPatch(conv.conv_id, { tag: { name: "" } });
    close();
  };

  const deleteCustomLabel = async (label) => {
    if (!label || label.predefined) return;
    if (!window.confirm(`¿Eliminar la etiqueta "${label.name}"? Las consultas con esta etiqueta quedarán sin etiqueta.`)) return;
    try {
      await axios.delete(`${API}/legal/labels/${encodeURIComponent(label.name)}`);
      setLabels((prev) => prev.filter((l) => l.name !== label.name));
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo eliminar la etiqueta";
      window.alert(msg);
    }
  };

  const createLabel = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) return;
    try {
      const resp = await axios.post(`${API}/legal/labels`, { name, color: newColor });
      const created = resp?.data;
      if (created) {
        setLabels((prev) => [...prev, created]);
        setNewName("");
        if (isManageMode) {
          // In manage mode, stay open and let user create more
          setCreating(false);
        } else {
          await pickTag(created);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo crear la etiqueta";
      window.alert(msg);
    }
  };

  if (!conv) return null;
  const currentTag = conv.tag?.name || "";

  return (
    <div
      data-testid="labels-modal"
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        background: closing ? "rgba(0,0,0,0)" : "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
        zIndex: 70,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        transition: "background 180ms ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 480,
          background: "var(--pl-card)",
          border: "1px solid rgba(0, 200, 255, 0.22)",
          borderRadius: "20px 20px 0 0",
          padding: "8px 16px calc(16px + env(safe-area-inset-bottom, 0px))",
          boxShadow: "0 -12px 40px rgba(0,0,0,0.45)",
          transform: closing ? "translateY(100%)" : "translateY(0)",
          transition: "transform 180ms ease",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            background: "rgba(255,255,255,0.18)",
            borderRadius: 999,
            margin: "4px auto 12px",
          }}
        />
        <h3
          style={{
            color: "var(--pl-text-strong)",
            fontSize: 18,
            fontWeight: 700,
            margin: "0 0 4px",
          }}
        >
          Etiquetas
        </h3>
        {isManageMode && (
          <p style={{ color: "var(--pl-text-muted)", fontSize: 12, margin: "0 0 12px" }}>
            Gestiona tus etiquetas. Puedes crear o eliminar las personalizadas.
          </p>
        )}

        {loading && (
          <div style={{ padding: "20px", color: "var(--pl-text-muted)", textAlign: "center" }}>
            Cargando…
          </div>
        )}

        {!loading && (
          <>
            <div
              data-testid="labels-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 10,
                marginBottom: 18,
              }}
            >
              {labels.map((lbl) => {
                const active = currentTag.toLowerCase() === lbl.name.toLowerCase();
                return (
                  <div
                    key={lbl.name}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "stretch",
                    }}
                  >
                    <button
                      type="button"
                      data-testid={`label-pick-${lbl.name}`}
                      onClick={() => pickTag(lbl)}
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: active
                          ? `1.5px solid ${lbl.color}`
                          : "1px solid var(--pl-border)",
                        background: active ? `${lbl.color}1f` : "var(--pl-card-soft)",
                        color: "var(--pl-text)",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: isManageMode ? "default" : "pointer",
                        textAlign: "left",
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: 999,
                          background: lbl.color,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {lbl.name}
                      </span>
                      {active && !isManageMode && (
                        <span style={{ color: lbl.color, fontWeight: 800, fontSize: 14 }}>
                          ✓
                        </span>
                      )}
                      {isManageMode && !lbl.predefined && (
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label="Eliminar etiqueta"
                          onClick={(e) => { e.stopPropagation(); deleteCustomLabel(lbl); }}
                          data-testid={`label-delete-${lbl.name}`}
                          style={{
                            color: "#ef4444",
                            cursor: "pointer",
                            padding: "2px 6px",
                            fontSize: 14,
                            lineHeight: 1,
                            fontWeight: 700,
                          }}
                        >
                          ✕
                        </span>
                      )}
                      {isManageMode && lbl.predefined && (
                        <span style={{ fontSize: 10, color: "var(--pl-text-subtle)", fontStyle: "italic" }}>
                          fija
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Create custom label */}
            <div
              style={{
                borderTop: "1px solid var(--pl-border)",
                paddingTop: 14,
                marginBottom: 14,
              }}
            >
              {!creating ? (
                <button
                  type="button"
                  data-testid="labels-create-toggle"
                  onClick={() => setCreating(true)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px dashed var(--pl-border-strong)",
                    background: "transparent",
                    color: "var(--pl-primary)",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    justifyContent: "center",
                  }}
                >
                  ＋ Crear etiqueta personalizada
                </button>
              ) : (
                <form
                  onSubmit={createLabel}
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  <input
                    type="text"
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    maxLength={30}
                    placeholder="Nombre de la etiqueta"
                    data-testid="labels-create-name"
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: "1px solid var(--pl-border-strong)",
                      background: "var(--pl-card-soft)",
                      color: "var(--pl-text)",
                      fontSize: 14,
                      outline: "none",
                    }}
                  />
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {CUSTOM_COLOR_PALETTE.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewColor(c)}
                        aria-label={`Color ${c}`}
                        data-testid={`labels-create-color-${c}`}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 999,
                          border: newColor === c ? "2.5px solid #fff" : "2px solid transparent",
                          background: c,
                          cursor: "pointer",
                          boxShadow: newColor === c ? `0 0 0 2px ${c}` : "none",
                          padding: 0,
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => { setCreating(false); setNewName(""); }}
                      style={{
                        flex: 1,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: "1px solid var(--pl-border)",
                        background: "transparent",
                        color: "var(--pl-text-muted)",
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={!newName.trim()}
                      data-testid="labels-create-submit"
                      style={{
                        flex: 1,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: "none",
                        background: newName.trim() ? "var(--pl-primary)" : "rgba(100,116,139,0.30)",
                        color: newName.trim() ? "#001018" : "#64748b",
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: newName.trim() ? "pointer" : "not-allowed",
                      }}
                    >
                      Crear
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Clear current tag */}
            {currentTag && !isManageMode && (
              <button
                type="button"
                data-testid="labels-clear"
                onClick={clearTag}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid var(--pl-border)",
                  background: "transparent",
                  color: "#ef4444",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  marginBottom: 8,
                }}
              >
                Quitar etiqueta actual
              </button>
            )}

            <button
              type="button"
              onClick={close}
              data-testid="labels-close"
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 14,
                border: "1px solid var(--pl-border)",
                background: "transparent",
                color: "var(--pl-text)",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cerrar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
